import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CalendarDays } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "@/lib/api";
import { formatDateIn } from "@/lib/datetime";
import { employeeApi, type EmployeeSchedule, type ScheduleDay } from "@/lib/employee-api";
import { notScheduledReason } from "@/lib/today-status";

/**
 * "When am I working?" — the employee's own roster (FEAT-2026-017).
 *
 * The wording here is load-bearing. A non-working day means attendance is not
 * *expected*, never that a check-in is refused (BR-202) — someone called in on
 * a Saturday still clocks in normally. So every label says "not scheduled" or
 * names the reason, and none of them say "closed" or "unavailable".
 */

/** Shared with the Today page, so the two never word the same day differently. */
function reasonLabel(day: ScheduleDay): string {
  return notScheduledReason(day) ?? "Not scheduled";
}

export default function MySchedule() {
  const [schedule, setSchedule] = useState<EmployeeSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSchedule(await employeeApi.schedule());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load your schedule");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Skeleton className="h-64 w-full" />;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Couldn't load your schedule</AlertTitle>
        <AlertDescription className="flex items-center gap-3">
          {error}
          <Button size="sm" variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const tz = schedule?.timezone ?? "Asia/Kuala_Lumpur";
  const days = schedule?.days ?? [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Next {days.length} days</CardTitle>
        {schedule?.defaultShiftName && (
          <p className="text-xs text-muted-foreground">
            You usually work {schedule.defaultShiftName}.
          </p>
        )}
      </CardHeader>
      <CardContent className="divide-y p-0">
        {days.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <CalendarDays className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No schedule to show.</p>
          </div>
        )}
        {days.map((day) => (
          <div
            key={day.date}
            className={`flex items-center justify-between gap-3 px-4 py-3 ${
              day.working ? "" : "bg-muted/30"
            }`}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{formatDateIn(day.date, tz)}</p>
              <p className="truncate text-xs text-muted-foreground">
                {day.working ? (day.shiftName ?? "Working") : reasonLabel(day)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {day.rostered && <Badge variant="outline">Rostered</Badge>}
              {day.working ? (
                <span className="text-sm tabular-nums">
                  {day.startTime && day.endTime ? `${day.startTime} – ${day.endTime}` : "—"}
                </span>
              ) : (
                <Badge variant="secondary">Off</Badge>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
